/**
 * MenuPalette — lewa kolumna buildera menu (ADR-0031).
 *
 * 4 kategorie items + 3 stałe drop'y:
 *   - Moduły (z subskrypcji catalog)
 *   - Niestandardowe moduły (custom modules show_in_menu=true)
 *   - Obszary zainstalowanych modułów (hub Kreatora Krok 4 — element installed_area)
 *   - Elementy systemowe (6 stałych — Users/Employees/Companies/Contractors/...)
 *   - + Custom link / + Sekcja / + Separator
 *
 * MVP UX: click = add do końca layout (top-level). Drag z palety = Etap E.
 * Items już dodane do hierarchy nadal są widoczne w paletce — admin może je
 * dodać wielokrotnie (każde wystąpienie to osobny MenuItem z własnym UUID).
 */

import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AdminMenuItem,
  AdminMenuSourceResponse,
  AdminSystemItemDefinition,
  MenuItem,
  MenuSourceFunctionalItem,
  MenuSourceKartoteka,
  MenuSourceResponse,
  SystemItemDefinition,
} from '@saas/shared';
import {
  Check,
  ChevronDown,
  GripVertical,
  Link as LinkIcon,
  Minus,
  PanelLeftClose,
  Plus,
  Square,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { PaletteSearchInput } from '@/components/modules/PaletteSearchInput';
import { Tooltip } from '@/components/Tooltip';
import { resolveIcon } from '@/lib/icon-map';
import { beginPaletteDrag, endPaletteDrag, PALETTE_DND_MIME } from '@/lib/menu-dnd';
import { normalizeSearch } from '@/lib/search-normalize';

/**
 * Generic source shape — akceptuje tenant `MenuSourceResponse` (z modules + customModules)
 * jak i admin `AdminMenuSourceResponse` (tylko systemItems). Modules/customModules
 * sekcje renderują się tylko gdy `allowModuleType` (tenant), więc admin nie potrzebuje
 * podawać pustych tablic.
 */
type MenuPaletteSource = MenuSourceResponse | AdminMenuSourceResponse;

export interface MenuPaletteProps {
  source: MenuPaletteSource | undefined;
  isLoading: boolean;
  /**
   * Callback przy dodaniu itemu. Union `MenuItem | AdminMenuItem` bo paletka generuje
   * oba shape'y zależnie od scope'u (admin nie generuje `module` type, ma 4-typ
   * AdminMenuItem; tenant generuje pełen 5-typ MenuItem). Caller (shell) trzyma
   * unionowy state hierarchy, więc shape pasuje.
   */
  onAdd: (item: MenuItem | AdminMenuItem) => void;
  /**
   * i18n key prefix (np. 'tenant.menuLayoutBuilder' albo 'admin.menuLayoutBuilder').
   * Default: 'tenant.menuLayoutBuilder' (back-compat dla istniejących callsiteów).
   */
  i18nPrefix?: string;
  /**
   * Czy renderować sekcje modułów (`modules` + `customModules`) z paletki.
   * Default: true (tenant). Admin scope przekazuje false — admin nie ma subscriptionów modułów.
   */
  allowModuleType?: boolean;
  /**
   * Tworzenie kartoteki własnej z palety (wariant A, decyzja 2026-07-03 — wzór huba
   * CreateKartotekaInline): „+" przy nagłówku sekcji KARTOTEKI otwiera inline formularz
   * (nazwa). Handler (strona tenanta) robi POST /modules + invalidację źródła i zwraca
   * slug+label utworzonej kartoteki — paleta od razu wstawia pozycję `module` do menu
   * (onAdd). Błąd = throw Error z przetłumaczonym komunikatem (paleta wyświetla pod polem).
   * Brak propa (admin / brak uprawnienia crm_core.custom_modules.manage) = brak „+".
   */
  onCreateKartoteka?: (name: string) => Promise<{ slug: string; label: string }>;
  /**
   * Szerokość kolumny w px (ręczne poszerzanie przez usera — uchwyt w shellu).
   * Undefined = domyślna szerokość z klasy (w-64). Inline style wygrywa z klasą.
   */
  widthPx?: number;
  /** Zwinięcie kolumny palety — ikonka w prawym górnym rogu nagłówka. Brak = bez ikonki. */
  onCollapse?: () => void;
  /**
   * Włącza przeciąganie elementów palety na płótno (natywny drag & drop, 2026-09-30).
   * Default false — plusy/klik dalej działają niezależnie od tej flagi. Kreator tenanta
   * i kreator modułów podają true; ITE zostaje przy samych plusach.
   */
  enableDrag?: boolean;
  /**
   * Zbiór tożsamości pozycji już obecnych w edytowanym menu (z `collectUsedPaletteKeys`).
   * Elementy palety w tym zbiorze dostają znacznik „w menu" + wyszarzenie — nadal można
   * je dodać (każde wystąpienie to osobna pozycja). Default: pusty zbiór.
   */
  usedKeys?: ReadonlySet<string>;
  /**
   * Źródło palety (kartoteki/funkcje) nie wczytało się: `forbidden` = 403 (brak uprawnień),
   * `failed` = inny błąd. Paleta pokazuje wtedy jednozdaniowy komunikat zamiast cicho
   * okrojonej listy (same elementy „Wstaw element"). Brak = źródło OK albo w trakcie ładowania.
   */
  loadError?: 'forbidden' | 'failed' | null;
  /**
   * Zakres zapamiętywania zwiniętych sekcji w przeglądarce. Default: `i18nPrefix`.
   * Kreatory dzielące prefiks tłumaczeń (kreator menu ITE i kreator menu szablonu modułu)
   * podają własny zakres, żeby zwinięcie w jednym nie zwijało sekcji w drugim.
   */
  collapsedStorageScope?: string;
}

export function MenuPalette({
  source,
  isLoading,
  onAdd,
  i18nPrefix = 'tenant.menuLayoutBuilder',
  allowModuleType = true,
  onCreateKartoteka,
  widthPx,
  onCollapse,
  enableDrag = false,
  usedKeys,
  loadError = null,
  collapsedStorageScope,
}: MenuPaletteProps) {
  const { t } = useTranslation();
  const usedLabel = t(`${i18nPrefix}.palette.usedInMenu`, { defaultValue: 'w menu' });
  const isUsed = (key: string | null): boolean => (key ? (usedKeys?.has(key) ?? false) : false);
  const kartoteki: MenuSourceKartoteka[] =
    source && 'kartoteki' in source ? (source.kartoteki ?? []) : [];
  // Pozycje funkcjonalne (np. „Decyzje" z Procesów) — osobna grupa, NIE kartoteki.
  const functional: MenuSourceFunctionalItem[] =
    source && 'functional' in source ? (source.functional ?? []) : [];
  // Jedna wyszukiwarka dla CAŁEJ palety (decyzja 2026-10-03, wariant A) — pod nagłówkiem,
  // przyklejona przy przewijaniu. Filtruje wszystkie sekcje (wstaw element, kartoteki — także
  // po nazwie modułu i slugu, funkcje, elementy systemowe), bez polskich znaków. Sekcja bez
  // trafień znika; podczas wyszukiwania sekcje z trafieniami są rozwinięte.
  const [query, setQuery] = useState('');
  // Inline formularz „Nowa kartoteka" (wariant A) — otwierany „+" przy nagłówku sekcji.
  const [createOpen, setCreateOpen] = useState(false);
  // Zwinięte sekcje — zapamiętane per przeglądarka (wygoda, nie stan biznesowy).
  const storageScope = collapsedStorageScope ?? i18nPrefix;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() =>
    readCollapsedSections(storageScope),
  );
  const q = normalizeSearch(query.trim());
  const searching = q.length > 0;
  const matches = (...texts: (string | null | undefined)[]): boolean =>
    !searching || texts.some((text) => !!text && normalizeSearch(text).includes(q));
  const isOpen = (id: string): boolean => searching || !collapsed[id];
  const toggleSection = (id: string): void =>
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      writeCollapsedSections(storageScope, next);
      return next;
    });

  const insertElements = [
    {
      key: 'section',
      icon: Square,
      label: t(`${i18nPrefix}.palette.section`),
      makeItem: () =>
        createItem({ type: 'section', label: t(`${i18nPrefix}.palette.newSectionDefaultLabel`) }),
      testId: 'palette-add-section',
    },
    {
      key: 'custom-link',
      icon: LinkIcon,
      label: t(`${i18nPrefix}.palette.customLink`),
      makeItem: () =>
        createItem({
          type: 'custom_link',
          label: t(`${i18nPrefix}.palette.newCustomLinkDefaultLabel`),
          url: t(`${i18nPrefix}.palette.newCustomLinkDefaultUrl`),
        }),
      testId: 'palette-add-custom-link',
    },
    {
      key: 'separator',
      icon: Minus,
      label: t(`${i18nPrefix}.palette.separator`),
      makeItem: () => createItem({ type: 'separator' }),
      testId: 'palette-add-separator',
    },
  ];
  const filteredInsert = insertElements.filter((e) => matches(e.label));
  const filteredKartoteki = kartoteki.filter((k) =>
    matches(k.label, k.slug, ...k.usedInModules.map((m) => m.name)),
  );
  const filteredFunctional = functional.filter((f) =>
    matches(f.label, ...(f.usedInModules ?? []).map((m) => m.name)),
  );
  const legacyVisible = allowModuleType && kartoteki.length === 0;
  const legacyModules =
    legacyVisible && source && 'modules' in source
      ? source.modules.filter((m) => matches(m.label))
      : [];
  const legacyCustomModules =
    legacyVisible && source && 'customModules' in source
      ? source.customModules.filter((m) => matches(m.label))
      : [];
  const legacyAreas =
    legacyVisible && source && 'installedAreas' in source
      ? (source.installedAreas ?? []).filter((a) => matches(a.label))
      : [];
  const systemItems = (source?.systemItems ?? []).filter(
    (s: SystemItemDefinition | AdminSystemItemDefinition) =>
      matches(t(s.labelI18nKey, s.defaultLabel)),
  );
  const showKartoteki =
    allowModuleType &&
    (kartoteki.length > 0 || !!onCreateKartoteka) &&
    // Przy wyszukiwaniu bez trafień sekcja zostaje, jeśli można tworzyć kartoteki — „szukam,
    // nie ma, tworzę" (i otwarty formularz nie traci wpisanej nazwy).
    (!searching || filteredKartoteki.length > 0 || !!onCreateKartoteka || createOpen);
  const nothingFound =
    searching &&
    !isLoading &&
    filteredInsert.length +
      (allowModuleType ? filteredKartoteki.length + filteredFunctional.length : 0) +
      legacyModules.length +
      legacyCustomModules.length +
      legacyAreas.length +
      systemItems.length ===
      0;
  const sectionProps = (id: string, title: string, count: number, total: number) => ({
    id,
    title,
    count,
    total,
    searching,
    open: isOpen(id),
    onToggle: () => toggleSection(id),
    toggleLabel: t(`${i18nPrefix}.palette.toggleSection`, {
      defaultValue: 'Zwiń lub rozwiń sekcję',
    }),
  });

  return (
    <aside
      className="flex w-64 shrink-0 flex-col gap-4 overflow-y-auto border-r border-border-secondary bg-bg-elevated p-4"
      style={widthPx ? { width: `${widthPx}px` } : undefined}
      data-testid="menu-palette"
    >
      {/* Nagłówek + wyszukiwarka przyklejone u góry jedynego paska przewijania palety. */}
      {/* Zwarty (recenzja UX 2026-10-03): nagłówek „Paleta" tylko dla czytników ekranu —
          na laptopie każdy piksel przyklejonej belki to mniej widocznych elementów. */}
      <div className="sticky -top-4 z-10 -mx-4 -mt-4 bg-bg-elevated px-4 pb-1 pt-4">
        <h2 className="sr-only">{t(`${i18nPrefix}.palette.header`)}</h2>
        <div className="flex items-start gap-1.5">
          <div className="min-w-0 flex-1">
            <PaletteSearchInput
              value={query}
              onChange={setQuery}
              placeholder={t(`${i18nPrefix}.palette.searchPlaceholder`, {
                defaultValue: 'Szukaj w palecie…',
              })}
              testId="palette-search"
            />
          </div>
          {onCollapse ? (
            <IconButton
              size="sm"
              onClick={onCollapse}
              aria-label={t(`${i18nPrefix}.hidePalette`, { defaultValue: 'Ukryj paletę' })}
              title={t(`${i18nPrefix}.hidePalette`, { defaultValue: 'Ukryj paletę' })}
              className="mt-0.5 text-text-muted"
              data-testid="menu-palette-collapse"
            >
              <PanelLeftClose />
            </IconButton>
          ) : null}
        </div>
      </div>

      {filteredInsert.length > 0 ? (
        <Section
          {...sectionProps(
            'insert',
            t(`${i18nPrefix}.palette.insertElement`),
            filteredInsert.length,
            insertElements.length,
          )}
        >
          {filteredInsert.map((e) => (
            <PaletteButton
              key={e.key}
              icon={e.icon}
              label={e.label}
              makeItem={e.makeItem}
              onAdd={onAdd}
              enableDrag={enableDrag}
              testId={e.testId}
            />
          ))}
        </Section>
      ) : null}

      {isLoading ? (
        <p className="text-xs text-text-muted">{t(`${i18nPrefix}.palette.loading`)}</p>
      ) : (
        <>
          {loadError ? (
            <p className="text-xs text-text-tertiary" data-testid="palette-load-error">
              {loadError === 'forbidden'
                ? t(`${i18nPrefix}.palette.sourceForbidden`, {
                    defaultValue: 'Brak dostępu do kartotek i funkcji.',
                  })
                : t(`${i18nPrefix}.palette.sourceFailed`, {
                    defaultValue: 'Nie udało się wczytać kartotek i funkcji.',
                  })}
            </p>
          ) : null}
          {/* Kreator menu tenanta (2026-07-02): paleta = płaska lista KARTOTEK z chipami
              modułów. Gdy backend zwraca kartoteki — sekcje modułów/obszarów znikają
              (kartoteki je zastępują); stare mocki bez pola renderują się po staremu.
              Lista NIE ma własnego paska przewijania — przewija się cała paleta (jak
              pozostałe palety kreatorów); długą listę zwija się nagłówkiem sekcji. */}
          {showKartoteki ? (
            <Section
              {...sectionProps(
                'kartoteki',
                t(`${i18nPrefix}.palette.kartoteki`, { defaultValue: 'Kartoteki' }),
                filteredKartoteki.length,
                kartoteki.length,
              )}
              action={
                onCreateKartoteka ? (
                  <IconButton
                    size="sm"
                    variant="subtle"
                    onClick={() => setCreateOpen((o) => !o)}
                    title={t(`${i18nPrefix}.palette.kartotekaCreateToggle`, {
                      defaultValue: 'Nowa kartoteka',
                    })}
                    aria-label={t(`${i18nPrefix}.palette.kartotekaCreateToggle`, {
                      defaultValue: 'Nowa kartoteka',
                    })}
                    className="h-6 w-6 rounded-md [&_svg]:h-3.5 [&_svg]:w-3.5"
                    data-testid="palette-kartoteka-create-toggle"
                  >
                    <Plus />
                  </IconButton>
                ) : null
              }
            >
              {onCreateKartoteka && createOpen ? (
                <CreateKartotekaInlineForm
                  i18nPrefix={i18nPrefix}
                  onCancel={() => setCreateOpen(false)}
                  onCreate={onCreateKartoteka}
                  onCreated={(created) => {
                    setCreateOpen(false);
                    // Wzór wariantu A: kartoteka od razu ląduje w edytowanym menu
                    // jako pozycja `module` (renderer rozwiąże po slugu — kind custom).
                    onAdd(
                      createItem({
                        type: 'module',
                        moduleCode: created.slug,
                        label: created.label,
                      }),
                    );
                  }}
                />
              ) : null}
              <div className="flex flex-col gap-1" data-testid="palette-kartoteki-list">
                {filteredKartoteki.map((k) => (
                  <KartotekaButton
                    key={`kart-${k.kind}-${k.slug}`}
                    kartoteka={k}
                    ownChipLabel={t(`${i18nPrefix}.palette.kartotekaOwnChip`, {
                      defaultValue: 'własna',
                    })}
                    enableDrag={enableDrag}
                    used={isUsed(`module:${k.slug}`)}
                    usedLabel={usedLabel}
                    makeItem={() =>
                      // Kartoteka ZAWSZE jako pozycja `module` po slugu — także dokumenty
                      // (faktury/umowy/pisma), które mają wewnętrzny `path`. Tylko tak dostają
                      // edycję Layoutu formularza i Widoku listy (resolveEntitySlug rozpoznaje
                      // wyłącznie `module`). Prawdziwe linki zewnętrzne robi osobny przycisk
                      // „+ Link"; pozycje funkcjonalne (Decyzje/KSeF) mają własną grupę.
                      createItem({
                        type: 'module',
                        moduleCode: k.slug,
                        label: k.label,
                        iconName: k.iconName ?? undefined,
                      })
                    }
                    onAdd={onAdd}
                  />
                ))}
              </div>
            </Section>
          ) : null}

          {allowModuleType && filteredFunctional.length > 0 ? (
            <Section
              {...sectionProps(
                'functional',
                t(`${i18nPrefix}.palette.functional`, { defaultValue: 'Funkcje' }),
                filteredFunctional.length,
                functional.length,
              )}
            >
              {filteredFunctional.map((f) => (
                <PaletteButton
                  key={`fn-${f.slug}`}
                  icon={resolveIcon(f.iconName)}
                  label={f.label}
                  enableDrag={enableDrag}
                  used={isUsed(`link:${f.path}`)}
                  usedLabel={usedLabel}
                  moduleNames={(f.usedInModules ?? []).map((m) => m.name)}
                  makeItem={() =>
                    createItem({
                      type: 'custom_link',
                      url: f.path,
                      label: f.label,
                      iconName: f.iconName ?? undefined,
                      external: false,
                    })
                  }
                  onAdd={onAdd}
                  testId={`palette-add-functional-${f.slug}`}
                />
              ))}
            </Section>
          ) : null}

          {legacyModules.length > 0 && source && 'modules' in source ? (
            <Section
              {...sectionProps(
                'modules',
                t(`${i18nPrefix}.palette.modules`),
                legacyModules.length,
                source.modules.length,
              )}
            >
              {legacyModules.map((m) => (
                <PaletteButton
                  key={`mod-${m.moduleCode}`}
                  icon={resolveIcon(m.iconName)}
                  label={m.label}
                  enableDrag={enableDrag}
                  used={isUsed(`module:${m.moduleCode}`)}
                  usedLabel={usedLabel}
                  makeItem={() =>
                    createItem({ type: 'module', moduleCode: m.moduleCode, label: m.label })
                  }
                  onAdd={onAdd}
                  testId={`palette-add-module-${m.moduleCode}`}
                />
              ))}
            </Section>
          ) : null}

          {legacyCustomModules.length > 0 && source && 'customModules' in source ? (
            <Section
              {...sectionProps(
                'custom-modules',
                t(`${i18nPrefix}.palette.customModules`),
                legacyCustomModules.length,
                source.customModules.length,
              )}
            >
              {legacyCustomModules.map((m) => (
                <PaletteButton
                  key={`cust-${m.moduleCode}`}
                  icon={resolveIcon(m.iconName)}
                  label={m.label}
                  enableDrag={enableDrag}
                  used={isUsed(`module:${m.moduleCode}`)}
                  usedLabel={usedLabel}
                  makeItem={() =>
                    createItem({ type: 'module', moduleCode: m.moduleCode, label: m.label })
                  }
                  onAdd={onAdd}
                  testId={`palette-add-custom-module-${m.moduleCode}`}
                />
              ))}
            </Section>
          ) : null}

          {legacyAreas.length > 0 && source && 'installedAreas' in source ? (
            <Section
              {...sectionProps(
                'installed-areas',
                t(`${i18nPrefix}.palette.installedAreas`),
                legacyAreas.length,
                source.installedAreas?.length ?? 0,
              )}
            >
              {legacyAreas.map((a) => (
                <PaletteButton
                  key={`area-${a.areaCode}`}
                  icon={resolveIcon(a.iconName)}
                  label={a.label}
                  enableDrag={enableDrag}
                  used={isUsed(`area:${a.areaCode}`)}
                  usedLabel={usedLabel}
                  makeItem={() =>
                    // Label ustawiany przy dodaniu (precedens: moduły) — canvas pokazuje
                    // nazwę obszaru; sidebar i tak preferuje item.label ?? nazwa z danych.
                    createItem({ type: 'installed_area', areaCode: a.areaCode, label: a.label })
                  }
                  onAdd={onAdd}
                  testId={`palette-add-installed-area-${a.areaCode}`}
                />
              ))}
            </Section>
          ) : null}

          {systemItems.length > 0 && source ? (
            <Section
              {...sectionProps(
                'system',
                t(`${i18nPrefix}.palette.systemItems`),
                systemItems.length,
                source.systemItems.length,
              )}
            >
              {systemItems.map((s: SystemItemDefinition | AdminSystemItemDefinition) => (
                <PaletteButton
                  key={`sys-${s.itemKey}`}
                  icon={resolveIcon(s.iconName)}
                  label={t(s.labelI18nKey, s.defaultLabel)}
                  enableDrag={enableDrag}
                  used={isUsed(`system:${s.itemKey}`)}
                  usedLabel={usedLabel}
                  makeItem={() =>
                    // Bez ustawiania `label` w hierarchy — renderer fallbackuje do t(labelI18nKey)
                    // co pozwala EN tłumaczenie zadziałać gdy się pojawi. Admin może świadomie
                    // nadpisać label przez properties panel.
                    createItem({
                      type: 'system_item',
                      itemKey: s.itemKey,
                    })
                  }
                  onAdd={onAdd}
                  testId={`palette-add-system-${s.itemKey}`}
                />
              ))}
            </Section>
          ) : null}

          {nothingFound ? (
            <div className="flex flex-col items-start gap-1.5" data-testid="palette-no-results">
              <p className="text-xs text-text-muted">
                {t(`${i18nPrefix}.palette.noResults`, {
                  defaultValue: 'Brak elementów pasujących do wyszukiwania.',
                })}
              </p>
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => setQuery('')}
                data-testid="palette-clear-search"
              >
                {t(`${i18nPrefix}.palette.clearSearch`, { defaultValue: 'Wyczyść wyszukiwanie' })}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/**
 * Zwinięte sekcje palety — osobno per zakres (`collapsedStorageScope`, domyślnie `i18nPrefix`):
 * kreator menu klienta, kreator menu ITE i kreator menu szablonu modułu pamiętają zwinięcie
 * niezależnie od siebie.
 */
const collapsedSectionsKey = (scope: string): string => `menuPalette.collapsedSections:${scope}`;

function readCollapsedSections(scope: string): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(collapsedSectionsKey(scope));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    // Tylko wpisy boolean — uszkodzone / obce dane w pamięci przeglądarki są pomijane.
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, boolean] => typeof entry[1] === 'boolean',
      ),
    );
  } catch {
    return {};
  }
}

function writeCollapsedSections(scope: string, value: Record<string, boolean>): void {
  try {
    window.localStorage.setItem(collapsedSectionsKey(scope), JSON.stringify(value));
  } catch {
    // Brak dostępu do localStorage (tryb prywatny) — zwinięcie działa do odświeżenia.
  }
}

/**
 * Sekcja palety — nagłówek-przycisk zwija/rozwija listę (strzałka + licznik). Podczas
 * wyszukiwania sekcja jest zawsze rozwinięta, a licznik pokazuje „trafienia / wszystkie".
 */
function Section({
  id,
  title,
  count,
  total,
  searching,
  open,
  onToggle,
  toggleLabel,
  action,
  children,
}: {
  id: string;
  title: string;
  count: number;
  total: number;
  searching: boolean;
  open: boolean;
  onToggle: () => void;
  toggleLabel: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const contentId = `palette-section-content-${id}`;
  return (
    <div className="flex flex-col gap-1.5" data-testid={`palette-section-${id}`}>
      <div className="flex items-center justify-between gap-2">
        {/* Wzorzec rozwijanej sekcji (WAI-ARIA disclosure): nagłówek NA ZEWNĄTRZ, przycisk
            w środku — czytnik ekranu widzi nagłówki sekcji. Podczas wyszukiwania sekcje są
            wymuszenie rozwinięte, więc przełącznik jest nieaktywny (aria-disabled, bez
            wypadania z kolejności Tab). */}
        <h3 className="min-w-0 flex-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={searching ? undefined : onToggle}
            aria-disabled={searching || undefined}
            aria-expanded={open}
            aria-controls={open ? contentId : undefined}
            title={toggleLabel}
            className="h-auto w-full min-w-0 justify-start gap-1.5 rounded-md px-0 py-0.5 text-left font-normal text-text-muted hover:bg-transparent hover:text-text-primary aria-disabled:cursor-default aria-disabled:hover:text-text-muted"
            data-testid={`palette-section-toggle-${id}`}
          >
            <ChevronDown
              className={`h-3.5 w-3.5 flex-shrink-0 transition-transform ${open ? '' : '-rotate-90'}`}
              aria-hidden
            />
            <span className="truncate text-[10px] font-semibold uppercase tracking-wider">
              {title}
            </span>
            <span
              className="flex-shrink-0 rounded-full bg-bg-tertiary px-1.5 py-0.5 text-[10px] text-text-secondary"
              data-testid={`palette-section-count-${id}`}
            >
              {searching ? `${count} / ${total}` : total}
            </span>
          </Button>
        </h3>
        {action}
      </div>
      {open ? (
        <div id={contentId} className="flex flex-col gap-1">
          {children}
        </div>
      ) : null}
    </div>
  );
}

function PaletteButton({
  icon: Icon,
  label,
  makeItem,
  onAdd,
  testId,
  enableDrag = false,
  used = false,
  usedLabel,
  moduleNames = [],
}: {
  icon: ReturnType<typeof resolveIcon>;
  label: string;
  makeItem: () => MenuItem | AdminMenuItem;
  onAdd: (item: MenuItem | AdminMenuItem) => void;
  testId?: string;
  enableDrag?: boolean;
  used?: boolean;
  usedLabel?: string;
  /** Moduły-właściciele pozycji — drugi rząd jak w KartotekaButton. Rozróżnia np.
   *  „Kontrahenci" z Handlu od systemowej kartoteki o tej samej nazwie. */
  moduleNames?: string[];
}) {
  const [dragging, setDragging] = useState(false);
  const dragProps = paletteDragProps(enableDrag, makeItem, setDragging);
  const hasModules = moduleNames.length > 0;
  return (
    <button
      type="button"
      onClick={() => onAdd(makeItem())}
      data-testid={testId}
      className={[
        'flex w-full items-center gap-2 rounded-md border border-border-secondary bg-bg-primary px-2.5 text-left text-sm text-text-secondary transition hover:border-accent-300 hover:text-text-primary',
        hasModules ? 'py-1' : 'py-1.5',
        enableDrag ? 'cursor-grab active:cursor-grabbing' : '',
        dragging ? 'opacity-40' : '',
        used ? 'opacity-70' : '',
      ].join(' ')}
      {...dragProps}
    >
      {enableDrag ? (
        <GripVertical className="h-3.5 w-3.5 flex-shrink-0 text-text-muted" aria-hidden />
      ) : null}
      <Icon className="h-4 w-4 flex-shrink-0 text-text-muted" />
      {hasModules ? (
        // Drugi rząd z modułem-właścicielem (jak KartotekaButton) — „Kontrahenci" z Handlu
        // nie wygląda już jak systemowa kartoteka o tej samej nazwie (szkolenie 14.10).
        <span className="flex min-w-0 flex-1 flex-col">
          <Tooltip content={`${label} — ${moduleNames.join(', ')}`}>
            <span className="line-clamp-2 break-words leading-5">{label}</span>
          </Tooltip>
          {used ? null : (
            <span
              className="truncate text-[11px] leading-4 text-text-muted"
              data-testid={testId ? `${testId}-modules` : undefined}
            >
              {moduleNames.join(' · ')}
            </span>
          )}
        </span>
      ) : (
        <Tooltip content={label}>
          <span className="flex-1 truncate text-left">{label}</span>
        </Tooltip>
      )}
      {used ? (
        <span className="flex flex-shrink-0 items-center gap-0.5 text-[10px] font-medium text-green-600">
          <Check className="h-3 w-3" />
          {usedLabel}
        </span>
      ) : null}
      <Plus className="h-3.5 w-3.5 flex-shrink-0 text-text-muted" />
    </button>
  );
}

/**
 * Handlery natywnego drag & drop dla elementu palety. Gdy `enableDrag=false` lub brak
 * `makeItem` — zwraca pusty obiekt (element nie jest przeciągalny). Na `dragstart`
 * buduje pozycję (z własnym UUID), zapamiętuje ją w module-singletonie i ustawia token
 * MIME; `dragend` sprząta.
 */
function paletteDragProps(
  enableDrag: boolean,
  makeItem: (() => MenuItem | AdminMenuItem) | undefined,
  setDragging: (v: boolean) => void,
): React.HTMLAttributes<HTMLElement> & { draggable?: boolean } {
  if (!enableDrag || !makeItem) return {};
  return {
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      const item = makeItem();
      beginPaletteDrag(item);
      e.dataTransfer.effectAllowed = 'copy';
      // Stały, neutralny znacznik — rozpoznanie źródła opiera się na obecności MIME
      // i module-singletonie, nie na wartości (surowy `item.type` mógłby wkleić się
      // jako tekst przy upuszczeniu poza płótnem, np. na pole edytowalne).
      e.dataTransfer.setData(PALETTE_DND_MIME, '1');
      setDragging(true);
    },
    onDragEnd: () => {
      endPaletteDrag();
      setDragging(false);
    },
  };
}

/**
 * Przycisk kartoteki w palecie — jak PaletteButton, ale z chipami modułów
 * („używana w: …"; brak modułów = chip „własna"). Klik wstawia pozycję
 * `module` po slugu kartoteki.
 */
function KartotekaButton({
  kartoteka,
  ownChipLabel,
  makeItem,
  onAdd,
  enableDrag = false,
  used = false,
  usedLabel,
}: {
  kartoteka: MenuSourceKartoteka;
  ownChipLabel: string;
  makeItem: () => MenuItem | AdminMenuItem;
  onAdd: (item: MenuItem | AdminMenuItem) => void;
  enableDrag?: boolean;
  used?: boolean;
  usedLabel?: string;
}) {
  const Icon = resolveIcon(kartoteka.iconName);
  const [dragging, setDragging] = useState(false);
  const dragProps = paletteDragProps(enableDrag, makeItem, setDragging);
  // Gdy pozycja jest już w menu, chip modułów ustępuje znacznikowi „w menu" (mniej szumu).
  const chips =
    kartoteka.usedInModules.length > 0
      ? kartoteka.usedInModules.map((m) => m.name)
      : [ownChipLabel];
  const chipText = chips.join(', ');
  // Dwa rzędy (recenzja UX 2026-10-03): w wąskiej palecie moduł obok nazwy ucinał ją do
  // „P…" — nazwa ma teraz cały pierwszy rząd, a moduł(y) albo „w menu" idą pod spód.
  return (
    <button
      type="button"
      onClick={() => onAdd(makeItem())}
      data-testid={`palette-add-kartoteka-${kartoteka.slug}`}
      className={[
        'flex w-full items-center gap-2 rounded-md border border-border-secondary bg-bg-primary px-2.5 py-1 text-left text-sm text-text-secondary transition hover:border-accent-300',
        enableDrag ? 'cursor-grab active:cursor-grabbing' : '',
        dragging ? 'opacity-40' : '',
        used ? 'opacity-70' : '',
      ].join(' ')}
      {...dragProps}
    >
      {enableDrag ? (
        <GripVertical className="h-3.5 w-3.5 flex-shrink-0 text-text-muted" aria-hidden />
      ) : null}
      <Icon className="h-4 w-4 flex-shrink-0 text-text-muted" />
      <span className="flex min-w-0 flex-1 flex-col">
        <Tooltip content={chipText ? `${kartoteka.label} — ${chipText}` : kartoteka.label}>
          <span className="line-clamp-2 break-words leading-5">{kartoteka.label}</span>
        </Tooltip>
        {used ? (
          <span className="flex items-center gap-0.5 text-[10px] font-medium text-green-600">
            <Check className="h-3 w-3" />
            {usedLabel}
          </span>
        ) : chips.length ? (
          <span className="flex min-w-0 items-center gap-1 overflow-hidden text-[11px] leading-4 text-text-muted">
            {chips.map((chip, i) => (
              <Fragment key={chip}>
                {i > 0 ? <span aria-hidden="true">·</span> : null}
                <span className="truncate">{chip}</span>
              </Fragment>
            ))}
          </span>
        ) : null}
      </span>
      <Plus className="h-3.5 w-3.5 flex-shrink-0 text-text-muted" />
    </button>
  );
}

/**
 * Inline formularz „Nowa kartoteka" (wariant A, wzór huba CreateKartotekaInline,
 * uproszczony do realiów tenanta): tylko nazwa — slug generuje backend
 * (POST /modules), pola i widoki konfiguruje się później (Ustawienia → Tabele
 * dodatkowe). Submit → onCreate (handler strony); sukces → onCreated (paleta
 * zamyka formularz i wstawia pozycję do menu); błąd → komunikat pod polem.
 */
function CreateKartotekaInlineForm({
  i18nPrefix,
  onCreate,
  onCreated,
  onCancel,
}: {
  i18nPrefix: string;
  onCreate: (name: string) => Promise<{ slug: string; label: string }>;
  onCreated: (created: { slug: string; label: string }) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canSubmit = name.trim().length > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await onCreate(name.trim());
      onCreated(created);
    } catch (e) {
      setError(
        e instanceof Error && e.message
          ? e.message
          : t(`${i18nPrefix}.palette.kartotekaCreateErrorGeneric`, {
              defaultValue: 'Nie udało się utworzyć kartoteki.',
            }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-1.5 rounded-md border border-border-secondary bg-bg-primary p-2"
      data-testid="palette-kartoteka-create-form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <label className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
        {t(`${i18nPrefix}.palette.kartotekaCreateNameLabel`, { defaultValue: 'Nazwa kartoteki' })}
      </label>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t(`${i18nPrefix}.palette.kartotekaCreateNamePlaceholder`, {
          defaultValue: 'np. Wnioski urlopowe',
        })}
        autoFocus
        disabled={submitting}
        className="w-full rounded-md border border-border-secondary bg-bg-elevated px-2 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent-500"
        data-testid="palette-kartoteka-create-name"
      />
      {error ? (
        <p className="text-xs text-red-600" data-testid="palette-kartoteka-create-error">
          {error}
        </p>
      ) : null}
      <p className="text-[11px] leading-snug text-text-muted">
        {t(`${i18nPrefix}.palette.kartotekaCreateHint`, {
          defaultValue:
            'Kartoteka powstanie jako własna i trafi do menu. Pola i widoki skonfigurujesz w Ustawienia → Tabele dodatkowe.',
        })}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-md bg-accent-600 px-2.5 py-1 text-xs font-medium text-white transition hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="palette-kartoteka-create-submit"
        >
          {t(`${i18nPrefix}.palette.kartotekaCreateSubmit`, { defaultValue: 'Utwórz' })}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-md px-2.5 py-1 text-xs text-text-secondary transition hover:text-text-primary"
          data-testid="palette-kartoteka-create-cancel"
        >
          {t('common.cancel', { defaultValue: 'Anuluj' })}
        </button>
      </div>
    </form>
  );
}

/**
 * Generator nowego itemu z paletki — typ widened do MenuItem | AdminMenuItem
 * żeby admin scope (4-value AdminSystemItemKey) mógł zwracać itemy bez TS
 * narrowing po stronie callsite.
 */
function createItem<T extends Omit<MenuItem, 'id'> | Omit<AdminMenuItem, 'id'>>(
  seed: T,
): T & { id: string } {
  return { ...seed, id: crypto.randomUUID() };
}
